import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { HttpRequestService } from 'src/app/core/services';

@Component({
  selector: 'app-youtube-videos',
  templateUrl: './youtube-videos.component.html',
  styleUrls: ['./youtube-videos.component.scss'],
})
export class YoutubeVideosComponent implements OnInit {
  tabIndex = 0;

  videos: any[] = [];
  loading = false;
  syncing = false;

  pageSize = 20;
  pageIndex = 1;
  skipIndex = 0;
  totalCount = 0;

  playlists: any[] = [];
  playlistLoading = false;

  playlistPageSize = 20;
  playlistPageIndex = 1;
  playlistSkipIndex = 0;
  playlistTotalCount = 0;

  previewVisible = false;
  previewTitle = '';
  previewUrl: SafeResourceUrl | null = null;

  constructor(
    private httpRequestService: HttpRequestService,
    private notificationService: NzNotificationService,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit(): void {
    // Initial load is triggered by the table's (nzQueryParams) on first render.
  }

  getVideos(
    skip: number = this.skipIndex,
    limit: number = this.pageSize,
  ): void {
    this.loading = true;
    const params = { skip, limit };
    this.httpRequestService
      .request('get', 'dashboards/get-videos', params)
      .subscribe(
        (result: any) => {
          this.videos = result?.data || result || [];
          this.totalCount = result?.totalCount ?? this.videos.length;
          this.loading = false;
        },
        () => {
          this.loading = false;
        },
      );
  }

  onQueryParamsChange(params: NzTableQueryParams): void {
    const { pageSize, pageIndex } = params;
    this.pageSize = pageSize;
    this.pageIndex = pageIndex;
    this.skipIndex = pageSize * (pageIndex - 1);
    this.getVideos(this.skipIndex, pageSize);
  }

  getPlaylists(
    skip: number = this.playlistSkipIndex,
    limit: number = this.playlistPageSize,
  ): void {
    this.playlistLoading = true;
    const params = { skip, limit };
    this.httpRequestService
      .request('get', 'dashboards/get-playlists', params)
      .subscribe(
        (result: any) => {
          this.playlists = result?.data || result || [];
          this.playlistTotalCount = result?.totalCount ?? this.playlists.length;
          this.playlistLoading = false;
        },
        () => {
          this.playlistLoading = false;
        },
      );
  }

  onPlaylistQueryParamsChange(params: NzTableQueryParams): void {
    const { pageSize, pageIndex } = params;
    this.playlistPageSize = pageSize;
    this.playlistPageIndex = pageIndex;
    this.playlistSkipIndex = pageSize * (pageIndex - 1);
    this.getPlaylists(this.playlistSkipIndex, pageSize);
  }

  getPlaylistVideos(playlist: any): any[] {
    return playlist?.videos || playlist?.items || [];
  }

  getPlaylistVideoCount(playlist: any): number {
    return playlist?.videoCount ?? this.getPlaylistVideos(playlist).length;
  }

  /** Playlists carry no thumbnail of their own — fall back to the first video. */
  getPlaylistThumbnail(playlist: any): string {
    if (playlist?.thumbnail) {
      return playlist.thumbnail;
    }
    const firstVideo = this.getPlaylistVideos(playlist)[0];
    return firstVideo ? this.getThumbnail(firstVideo) : '';
  }

  getPlaylistTitle(playlist: any): string {
    return playlist?.title || playlist?.playlistId || 'Untitled Playlist';
  }

  getPlaylistUrl(playlist: any): string {
    return playlist?.playlistId
      ? `https://www.youtube.com/playlist?list=${playlist.playlistId}`
      : '';
  }

  updatePlaylistStatus(id: string, currentStatus: boolean): void {
    this.httpRequestService
      .request('put', `dashboards/update-playlist-status/${id}`, {
        status: !currentStatus,
      })
      .subscribe(
        (result: any) => {
          this.getPlaylists(this.playlistSkipIndex, this.playlistPageSize);
          this.notificationService.success(
            '',
            result?.message || 'Status updated successfully',
          );
        },
        () => {
          this.notificationService.error('', 'Failed to update status');
        },
      );
  }

  syncVideos(): void {
    this.syncing = true;
    this.httpRequestService.request('post', 'dashboards/sync-videos').subscribe(
      (result: any) => {
        this.syncing = false;
        this.notificationService.success(
          '',
          result?.message || 'Videos synced successfully',
        );
        this.pageIndex = 1;
        this.skipIndex = 0;
        this.getVideos();
      },
      () => {
        this.syncing = false;
        this.notificationService.error('', 'Failed to sync videos');
      },
    );
  }

  /** Extracts the YouTube video id from any common URL shape. */
  getYoutubeId(video: any): string {
    const raw =
      video?.videoId ||
      video?.youtubeId ||
      video?.url ||
      video?.link ||
      video?.videoUrl ||
      '';
    if (!raw) {
      return '';
    }
    const match = raw.match(
      /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/))([\w-]{11})/,
    );
    if (match) {
      return match[1];
    }
    // Already a bare id.
    return /^[\w-]{11}$/.test(raw) ? raw : '';
  }

  /** Public watch URL for "Open in web". */
  getWatchUrl(video: any): string {
    const id = this.getYoutubeId(video);
    if (id) {
      return `https://www.youtube.com/watch?v=${id}`;
    }
    return video?.url || video?.link || video?.videoUrl || '';
  }

  /** Reads a numeric stat, tolerating both flat and nested `statistics` shapes. */
  private getStat(video: any, keys: string[]): number {
    const stats = video?.statistics || {};
    for (const key of keys) {
      const value = video?.[key] ?? stats?.[key];
      if (value !== undefined && value !== null) {
        return Number(value) || 0;
      }
    }
    return 0;
  }

  getViewCount(video: any): number {
    return this.getStat(video, ['viewCount', 'views']);
  }

  getLikeCount(video: any): number {
    return this.getStat(video, ['likeCount', 'likes']);
  }

  getDislikeCount(video: any): number {
    return this.getStat(video, ['dislikeCount', 'dislikes']);
  }

  getCommentCount(video: any): number {
    return this.getStat(video, ['commentCount', 'comments']);
  }

  updateStatus(id: string, newStatus: boolean): void {
    console.log('Updating status for video ID:', id, 'New Status:', newStatus);
    this.httpRequestService
      .request('put', `dashboards/update-video-status/${id}`, {
        status: !newStatus,
      })
      .subscribe(
        (result: any) => {
          this.getVideos(this.skipIndex, this.pageSize);
          this.notificationService.success(
            '',
            result?.message || 'Status updated successfully',
          );
        },
        () => {
          this.notificationService.error('', 'Failed to update status');
        },
      );
  }

  getThumbnail(video: any): string {
    if (video?.thumbnail) {
      return video.thumbnail;
    }
    const id = this.getYoutubeId(video);
    return id ? `https://img.youtube.com/vi/${id}/mqdefault.jpg` : '';
  }

  openPlaylistPreview(playlist: any): void {
    const playlistId = playlist?.playlistId;
    if (!playlistId) {
      this.notificationService.error('', 'No valid playlist to preview');
      return;
    }
    // Start from the first video so the player shows content immediately,
    // while `list` queues the whole playlist.
    const firstId = this.getYoutubeId(this.getPlaylistVideos(playlist)[0]);
    const embed = firstId
      ? `https://www.youtube.com/embed/${firstId}?list=${playlistId}`
      : `https://www.youtube.com/embed/videoseries?list=${playlistId}`;
    this.previewTitle = this.getPlaylistTitle(playlist);
    this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(embed);
    this.previewVisible = true;
  }

  openPreview(video: any): void {
    const id = this.getYoutubeId(video);
    if (!id) {
      this.notificationService.error('', 'No valid YouTube link to preview');
      return;
    }
    this.previewTitle = video?.title || 'Video Preview';
    this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      `https://www.youtube.com/embed/${id}`,
    );
    this.previewVisible = true;
  }

  closePreview(): void {
    this.previewVisible = false;
    this.previewUrl = null;
  }

  onDropVideos(event: CdkDragDrop<any[]>): void {
    if (event.previousIndex === event.currentIndex) return;

    moveItemInArray(this.videos, event.previousIndex, event.currentIndex);

    const reorderedPayload = this.videos.map((item, index) => ({
      id: item._id,
      order: index + this.skipIndex,
    }));

    this.httpRequestService
      .request('put', 'dashboards/reorder-videos', {
        reorderedItems: reorderedPayload,
      })
      .subscribe({
        next: () => {
          this.notificationService.success('', 'Order Updated Successfully');
          this.getVideos();
        },
        error: () => {
          this.getVideos(this.skipIndex, this.pageSize);
        },
      });
  }
}
